import React, { useEffect, useState, useCallback, useRef, useLayoutEffect } from "react";
import * as d3 from "d3";
import * as venn from "venn.js";
import { GET_DATAGUARD_ROLES_DATA } from "../Utility/DataguardConstant";
import { useDispatch, useSelector } from 'react-redux';
import Page_Loader from '../../../assets/vizr_images/page_loader.gif';
import { isEmpty } from "lodash";
import { maskUsername } from "../../SysGuard/SysGuardCommonComp"
import { calculatePercentage } from "../../SysGuard/sysGuardConstants";

// Custom hook to track window size
function useWindowSize() {
  const [size, setSize] = useState([0, 0]);
  useLayoutEffect(() => {
    function updateSize() {
      setSize([window.innerWidth, window.innerHeight]);
    }
    window.addEventListener('resize', updateSize);
    updateSize();
    return () => window.removeEventListener('resize', updateSize);
  }, []);
  return size;
}

const DataguardRoles = () => {
  const dispatch = useDispatch();
  const rolesData = useSelector((state) => state.DataguardReducer.rolesData);
  const rolesLoader = useSelector((state) => state.DataguardReducer.rolesLoader);
  const fromTimeStamp = useSelector((state) => state.DataguardReducer.fromTimeStamp);
  const toTimeStamp = useSelector((state) => state.DataguardReducer.toTimeStamp);
  const vennRef = useRef(null);
  
  // Track window size for responsive chart rendering
  const [windowWidth, windowHeight] = useWindowSize();

  const [sets, setSets] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  useEffect(() => {
    if (fromTimeStamp && toTimeStamp) {
      dispatch({
        type: GET_DATAGUARD_ROLES_DATA,
        payload: {
          fromTimeStamp,
          toTimeStamp
        }
      });
    }
  }, [dispatch, fromTimeStamp, toTimeStamp]);

  useEffect(() => {
    if (rolesData && rolesData.combinations) {
      // Calculate total users for percentage calculation
      const totalUsers = rolesData.combinations.reduce((total, combination) => {
        if (combination.sets.length === 1) {
          return total + combination.size;
        }
        return total;
      }, 0);
      const modifiedData = rolesData.combinations.map(item => {
        const percentage = calculatePercentage(item.size, totalUsers);
        return {
          ...item,
          tooltip: item.label,
          percentage: percentage,
          count: item.size,
          label: ' ',
        };
      });
      setSets(modifiedData);
      setTotalUsers(totalUsers);
    }
  }, [rolesData]);

  useEffect(() => {
    if (!rolesLoader) {
      renderChart();
    } else {
      // Clear the chart when loader is active
      if (vennRef.current) {
        d3.select(vennRef.current).selectAll("*").remove();
      }
    }
  }, [sets, totalUsers, windowWidth, windowHeight, rolesLoader]);

  const getUXColors = () => {
    // Define primary UX colors matching the design
    const primaryColors = [
      '#8B5CF6', // Bright Purple
      '#F97316', // Bright Orange  
      '#06B6D4', // Cyan Blue
      '#EF4444', // Bright Red
      '#10B981', // Emerald Green
      '#F59E0B', // Amber Yellow
      '#EC4899', // Hot Pink
      '#3B82F6', // Blue
      '#84CC16', // Lime Green
      '#8B5A2B', // Brown
      '#6366F1', // Indigo
      '#14B8A6'  // Teal
    ];

    // Get individual roles count
    const individualRoles = sets.filter(set => set.sets.length === 1);
    let uxColors = [...primaryColors];

    // If more than 10 roles, add D3 colors for the additional ones
    if (individualRoles.length > 10) {
      const additionalColorsNeeded = individualRoles.length - 10;

      // Use D3's color schemes for additional colors
      const d3ColorSchemes = [
        d3.schemeCategory10,
        d3.schemeSet1,
        d3.schemeSet2,
        d3.schemeSet3,
        d3.schemePastel1,
        d3.schemePastel2,
        d3.schemeDark2,
        d3.schemeAccent
      ];

      // Flatten all D3 color schemes and remove duplicates
      const allD3Colors = [...new Set(d3ColorSchemes.flat())];

      // Add D3 colors for roles beyond the 10th
      for (let i = 0; i < additionalColorsNeeded && i < allD3Colors.length; i++) {
        uxColors.push(allD3Colors[i]);
      }
    }
    return uxColors;
  }

  const renderLabel = (group, d, labelsGroup) => {
    // Get the existing properties from the group's text element
    const existingText = group;
    const x = existingText.attr("x") || 0;
    const y = existingText.attr("y") || 0;
    const dx = existingText.attr("dx") || 0;
    const dy = existingText.attr("dy") || 0;
    labelsGroup
      .append("text")
      .attr("class", "venn-label")
      .attr("x", x)
      .attr("y", y)
      .attr("dx", dx)
      .attr("dy", dy)
      .text(d.count ? `(${d.count})` : '')
      .on("mouseover", function (event) {
        const tooltip = d3.select(".venn-tooltip");
        tooltip.transition().duration(200).style("opacity", 0.9);
        tooltip.html(
          `<strong>${d.tooltip || d.label || ""}</strong><br/>Users: ${d.size}`
        )
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
        d3.select(this).style("cursor", "pointer");
      })
      .on("mousemove", function (event) {
        const tooltip = d3.select(".venn-tooltip");
        tooltip
          .style("left", (event.pageX + 10) + "px")
          .style("top", (event.pageY - 28) + "px");
      })
      .on("mouseout", function () {
        d3.select(".venn-tooltip").transition().duration(200).style("opacity", 0);
      })
      .style("fill", "black");
  };

  const renderChart = () => {
    try {
      if (!vennRef.current) return;
      if (sets.length === 0) return;

      const containerWidth = vennRef.current.offsetWidth;
      const containerHeight = vennRef.current.offsetHeight;

      // Don't render if container doesn't have dimensions yet
      if (containerWidth === 0 || containerHeight === 0) {
        return;
      }
      // Clear any existing chart
      d3.select(vennRef.current).selectAll("*").remove();
      const uxColors = getUXColors();

      // Create Venn diagram with responsive dimensions
      const chart = venn.VennDiagram()
        .width(containerWidth)
        .height(containerHeight);

      // Render the chart
      const vennDiagram = d3.select(vennRef.current)
        .datum(sets)
        .call(chart);
      applyColorToCircles(vennDiagram, uxColors);
      // Add tooltip div
      // renderTooltip(vennDiagram);
      renderArcs(vennDiagram, uxColors);
      
      // Style text labels to be white
      const tooltip = d3.select("body")
        .append("div")
        .attr("class", "venn-tooltip")
        .style("position", "absolute")
        .style("text-align", "center")
        .style("width", "auto")
        .style("padding", "8px")
        .style("font", "12px sans-serif")
        .style("background", "#FFFFFF")
        .style("color", "#000")
        .style("border", "0px")
        .style("border-radius", "4px")
        .style("pointer-events", "none")
        .style("z-index", 1000)
        .style("opacity", 0);

      const labelsGroup = d3.select(vennRef.current).select("svg").append("g")
        .attr("class", "labels-on-top");
      
      vennDiagram.selectAll("g.venn-area.venn-circle text.label")
        .each(function (d, i) {
          const group = d3.select(this);
          renderLabel(group, d, labelsGroup);
        });
    } catch (error) {
      console.error("Error rendering Venn diagram:", error);
      d3.select(vennRef.current).selectAll("*").remove();
      d3.select(vennRef.current).append("div")
        .attr("class", "no-data-message")
        .text("No data available for the selected time range.")
    }
  };

  const applyColorToCircles = (vennDiagram, uxColors) => {
    let individualRoleIndex = 0;
    vennDiagram.selectAll("g")
      .each(function (d, i) {
        if (d.sets.length === 1) { // Only for individual circles
          const group = d3.select(this);
          const pathElement = group.select("path");

          if (!pathElement.empty()) {
            const circleColor = uxColors[individualRoleIndex];
            pathElement
              .style("fill", circleColor)
              .style("fill-opacity", 0.8);

            individualRoleIndex++;
          }
        }
      });
  };
  const renderArcs = (vennDiagram, uxColors) => {
    let arcRoleIndex = 0;
    vennDiagram.selectAll("g")
      .each(function (d, i) {
        if (d.sets.length > 1) return; // Only for single sets (individual circles)

        const group = d3.select(this);
        const pathElement = group.select("path");

        if (!pathElement.empty()) {
          // Get the circle's center and radius
          const pathData = pathElement.attr("d");
          const pathNode = pathElement.node();
          const bbox = pathNode.getBBox();
          const centerX = bbox.x + bbox.width / 2;
          const centerY = bbox.y + bbox.height / 2;
          const radius = Math.min(bbox.width, bbox.height) / 2;

          // Calculate percentage for arc length (based on original size)
          const percentage = calculatePercentage(d.size, totalUsers);

          // Get the circle's color
          const circleColor = uxColors[arcRoleIndex];

          // Create arc generator
          const arcRadius = radius + 5; // Reduced gap - closer to the main circle
          const arcWidth = 4; // Thickness of the arc

          // Calculate arc angles (starting from 0 degrees - right side, going clockwise)
          const startAngle = 0; // Start at 0 degrees (3 o'clock position)
          const endAngle = startAngle + (2 * Math.PI * percentage / 100); // Arc length based on percentage

          const arc = d3.arc()
            .innerRadius(arcRadius - arcWidth / 2)
            .outerRadius(arcRadius + arcWidth / 2)
            .startAngle(startAngle)
            .endAngle(endAngle);

          // Add the percentage arc
          group.insert("path", "path")
            .attr("d", arc)
            .attr("transform", `translate(${centerX}, ${centerY})`)
            .attr("fill", circleColor)
            .attr("opacity", 0.8)
            .style("pointer-events", "none");

          arcRoleIndex++;
        }
      });
  };
  const renderLoader = () => {
    if (rolesLoader) {
      return (
        <div className='loader-container'>
          <img src={Page_Loader} alt="Loading..." className='loader-img' />
        </div>
      );
    } else if (isEmpty(rolesData) || isEmpty(sets)) {
        return <div className="no-data-message">No data available for the selected time range.</div>;
    }
  };
  return (
    <>
      {renderLoader()}
      <div
        ref={vennRef}
        style={{
          width: "100%",
          height: "100%",
        }}
      />
    </>
  );

};

export default DataguardRoles;